import { readPref, savePref } from '@shared/services/prefsService'

/**
 * How a person has arranged the pages -- the project's chat widened or
 * folded, the skills rail shut, the page a board was left on, a new frame's
 * colour, the Usage page's two switches -- kept with their account
 * (Huanrui, 2026-10-02), as one setting, 'ui', holding them all.
 *
 * The browser keeps its copy as before (useRemembered, and the readers that
 * use these keys), so every page is drawn at once from it. Once a visit the
 * account's copy is read and wins for anything not changed in the meantime;
 * a change made before it arrives is not lost, and is sent with the rest.
 */

const listeners = new Map<string, Set<(value: unknown) => void>>()
/** What the account holds, once read, with this visit's changes on top. */
let held: Record<string, unknown> = {}
/** Changed in this visit before the account's copy arrived. */
const changed = new Set<string>()
let loaded = false
let loading = false

function writeLocal(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // The account's copy still has it.
  }
}

/** A remembered value, as this browser has it (JSON, or a bare string from before). */
export function readUi<T>(key: string, fallback: T, valid: (value: unknown) => boolean): T {
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null) return fallback
    let parsed: unknown = raw
    try {
      parsed = JSON.parse(raw)
    } catch {
      // A bare string, written before these were kept as JSON.
    }
    return valid(parsed) ? (parsed as T) : fallback
  } catch {
    return fallback
  }
}

/** Remember a value: here at once, with the account soon after. */
export function writeUi(key: string, value: unknown) {
  writeLocal(key, value)
  held = { ...held, [key]: value }
  if (loaded) savePref('ui', held, 800)
  else changed.add(key)
}

/** Told when the account's copy changes a value this page is showing. */
export function watchUi(key: string, listener: (value: unknown) => void): () => void {
  let set = listeners.get(key)
  if (!set) listeners.set(key, (set = new Set()))
  set.add(listener)
  return () => set?.delete(listener)
}

/** Once a visit, once signed in: take what the account remembers. */
export function adoptAccountUi() {
  if (loaded || loading) return
  loading = true
  readPref('ui')
    .then((value) => {
      const account = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
      for (const [key, kept] of Object.entries(account)) {
        if (changed.has(key)) continue
        writeLocal(key, kept)
        for (const listener of listeners.get(key) ?? []) listener(kept)
      }
      const mine = Object.fromEntries([...changed].map((key) => [key, held[key]]))
      held = { ...account, ...mine }
      loaded = true
      if (changed.size > 0) savePref('ui', held, 0)
      changed.clear()
    })
    .catch(() => undefined)
    .finally(() => {
      loading = false
    })
}
